/**
 * Metadata-options seed (Plane #364, frontend half). Provides the `initialData`
 * for `useOptions()` so every backend-owned `<select>` renders synchronously on
 * first paint — no empty-select flash, no loading state — before the network
 * `GET /metadata/options/` resolves.
 *
 * Shape mirrors the backend response exactly: `{ <group>: [{ value, label }] }`
 * for the 16 bounded choice groups. The BACKEND is the source of truth for
 * labels; this seed is aligned to the backend labels so `initialData === fetched`
 * and nothing visibly changes once the query resolves. Where this seed differs
 * from the prior inline frontend label (e.g. donor `Fully Parted` casing), the
 * delta is intentional and reported in the #364 migration notes.
 *
 * Several groups derive their raw value/label data from the existing
 * `src/constants/*` files (the historical single-source modules); the runtime
 * source is now the `useOptions()` hook, and those constant files remain only as
 * this seed's data source plus the frontend-only display layers they still own
 * (channel short labels, COPY_CHANNELS, pill order, plan features, role chips,
 * invite copy, notification in-app flags).
 */

import { CARRIERS } from './carriers';
import { CHANNELS, SALE_CHANNELS } from './channels';
import { CONDITIONS, SIDES } from './parts';
import { SHIPMENT_STATUSES } from './shipmentStatuses';

/** `[key, label]` tuples → `[{ value, label }]` (backend response shape). */
const fromTuples = (tuples) => tuples.map(([value, label]) => ({ value, label }));

/**
 * Backend-aligned seed for the 16 `GET /metadata/options/` groups. Order matches
 * the backend `OPTION_GROUPS` enum order (select-option order is preserved).
 */
const METADATA_OPTIONS_SEED = {
  // inventory
  part_status: fromTuples([
    ['draft', 'Draft'],
    ['available', 'Available'],
    ['reserved', 'Reserved'],
    ['sold', 'Sold'],
    ['removed', 'Removed'],
  ]),
  part_condition: fromTuples(CONDITIONS),
  part_side: fromTuples(SIDES),
  // CHANNELS carries a 3rd "short" element; take only key + full name here. The
  // short labels stay a frontend-only display layer in channels.js.
  channel: CHANNELS.map(([value, label]) => ({ value, label })),
  // sales
  sale_status: fromTuples([
    ['completed', 'Completed'],
    ['returned', 'Returned'],
    ['voided', 'Voided'],
  ]),
  cleanup_status: fromTuples([
    ['pending', 'Pending'],
    ['complete', 'Complete'],
  ]),
  sale_channel: fromTuples(SALE_CHANNELS),
  carrier: fromTuples(CARRIERS),
  shipment_status: fromTuples(SHIPMENT_STATUSES),
  // sellers
  membership_role: fromTuples([
    ['owner', 'Owner'],
    ['manager', 'Manager'],
    ['lister', 'Lister'],
    ['viewer', 'Viewer'],
  ]),
  subscription_plan: fromTuples([
    ['trial', 'Trial'],
    ['starter', 'Starter'],
    ['shop', 'Shop'],
    ['pro', 'Pro'],
  ]),
  seller_segment: fromTuples([
    ['part_out', 'Part out cars'],
    ['loose_parts', 'Sell loose parts'],
  ]),
  membership_status: fromTuples([
    ['active', 'Active'],
    ['invited', 'Invited'],
    ['revoked', 'Revoked'],
  ]),
  invitation_status: fromTuples([
    ['pending', 'Pending'],
    ['accepted', 'Accepted'],
    ['revoked', 'Revoked'],
  ]),
  // notifications
  notification_event: fromTuples([
    ['risk_created', 'Double-sell risk'],
    ['invite_accepted', 'Invitation accepted'],
    ['weekly_summary', 'Weekly summary'],
  ]),
  // garage
  donor_status: fromTuples([
    ['active', 'Active'],
    ['fully_parted', 'Fully Parted'],
    ['archived', 'Archived'],
  ]),
};

export { METADATA_OPTIONS_SEED };
